import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/primitives";

// Blocking Terms re-acceptance gate. Shows when the current Terms version is newer
// than what the member last accepted. A member may snooze ONCE per version; after
// that the prompt is fully blocking (no "remind me later").
export default function TermsGate() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [snoozed, setSnoozed] = useState(false);

  const { data } = useQuery({
    queryKey: ["terms-status"],
    queryFn: () => api("/legal/terms-status"),
    enabled: !!user?.user_id,
    refetchOnWindowFocus: false,
  });

  const accept = useMutation({
    mutationFn: () => api("/legal/accept-terms", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["terms-status"] }),
  });
  const snooze = useMutation({
    mutationFn: () => api("/legal/snooze-terms", { method: "POST" }),
    onSuccess: () => { setSnoozed(true); qc.invalidateQueries({ queryKey: ["terms-status"] }); },
  });

  if (!user?.user_id || !data?.needs_acceptance || snoozed) return null;
  const canSnooze = !!data.can_snooze;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="terms-gate">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-line shadow-xl p-6">
        <div className="w-11 h-11 rounded-xl bg-surface-2 flex items-center justify-center mb-3">
          <ShieldCheck className="w-6 h-6 text-brand" />
        </div>
        <h2 className="font-serif text-xl text-ink">We&apos;ve updated our Terms</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Our Terms &amp; Conditions have changed, including how we work with our payment, payroll and
          bank-connection partners (Stripe, Gusto and Plaid). Please review and accept the updated Terms to continue.
        </p>
        <button
          onClick={() => nav("/legal?doc=terms")}
          className="text-sm text-brand hover:underline mt-3 inline-block"
          data-testid="terms-gate-review"
        >
          Review the updated Terms &amp; Conditions →
        </button>
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <Button onClick={() => accept.mutate()} disabled={accept.isPending} data-testid="terms-gate-accept">
            {accept.isPending ? "Saving…" : "I agree"}
          </Button>
          {canSnooze && (
            <Button variant="outline" onClick={() => snooze.mutate()} disabled={snooze.isPending} data-testid="terms-gate-snooze">
              Remind me later
            </Button>
          )}
        </div>
        {!canSnooze && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="terms-gate-required">
            You need to accept the updated Terms to keep using Two Degrees.
          </p>
        )}
      </div>
    </div>
  );
}
